/**
 * CandidateReveal — Plan 10-05 (D-11).
 *
 * The full-screen ceremonial reveal of an Anchor (or milestone) candidate
 * set. SPA-driven, not agent-driven (RESEARCH Open Question 2): the candidate
 * tiles fill in off the `vision.generation_succeeded` Reverb broadcasts for
 * the set; tiles render as `card` + `skeleton` while still pending.
 *
 * The user picks one primary ("Als Hauptbild wählen") and may keep extras
 * ("Behalten"). Confirming POSTs to /api/visions/{candidateSet}/select and
 * shows the discard-confirmation Dialog (D-06, --destructive confirm) before
 * the unselected candidates are discarded.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ImageZoom } from '@/components/ui/ImageZoom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api-client';
import {
  useReverbChannel,
  type VisionFailedPayload,
  type VisionSucceededPayload,
} from '@/hooks/useReverbChannel';
import { trackEvent } from '@/lib/telemetry';

/** One candidate image in the set. */
export interface VisionCandidate {
  id: string;
  media_registry_id: string;
  image_url: string;
}

interface CandidateRevealProps {
  /** The candidate-set id the selection POST is scoped to. */
  candidateSet: string;
  /** Candidates already produced when the reveal mounts. */
  initialCandidates?: VisionCandidate[];
  /** Total candidates expected in the set — drives the pending skeleton count. */
  expectedCount: number;
  /**
   * The media ids of every slot in this set, produced or not (Plan 14-14).
   *
   * This is what makes a broadcast frame attributable. Frames arrive on
   * `user.{uuid}`, which carries every generation this user has in flight, not
   * only the ones this reveal is showing; without the set's own ids there is no
   * way to tell a candidate of this set from a regeneration that happens to
   * land while the overlay is open.
   */
  expectedMediaIds?: string[];
  /**
   * Asks the parent to re-read the pending payload.
   *
   * The reveal stays prop-driven and does not fetch (10-05). It cannot build a
   * usable tile from a broadcast frame on its own: the frame carries an R2
   * object key, and what an `<img>` needs is a signed URL, which only the
   * server mints. So a frame is a signal to refetch, not a tile.
   */
  onCandidateArrived?: () => void;
  /** Called after a confirmed selection so the parent can route to the gallery. */
  onConfirmed?: () => void;
}

/**
 * Where one set's in-progress selection lives. Per set, so choosing in a later
 * reveal cannot be pre-filled from an older one.
 */
function selectionKey(candidateSet: string): string {
  return `becoming.reveal.selection.${candidateSet}`;
}

export function CandidateReveal({
  candidateSet,
  initialCandidates = [],
  expectedCount,
  expectedMediaIds = [],
  onCandidateArrived,
  onConfirmed,
}: CandidateRevealProps) {
  const { t } = useTranslation();
  const [candidates, setCandidates] =
    useState<VisionCandidate[]>(initialCandidates);
  const [primaryId, setPrimaryId] = useState<string | null>(null);
  const [keptIds, setKeptIds] = useState<Set<string>>(new Set());
  // Restored from storage before the default-to-first effect can run, so a
  // reload does not silently reset the choice to candidate one.
  const restoredRef = useRef(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  /**
   * Media ids of this set that terminally failed. A set rather than a counter
   * because the same frame can arrive twice and a resolved slot must not be
   * subtracted twice.
   */
  const [failedIds, setFailedIds] = useState<Set<string>>(new Set());

  const tracked = useMemo(() => new Set(expectedMediaIds), [expectedMediaIds]);

  /**
   * Does this frame belong to the set on screen?
   *
   * When the id list is empty we cannot tell, and the answer is no rather than
   * yes: acting on an unattributable frame would remove a slot for an image
   * that may still be coming. The server always sends the list
   * (VisionGalleryController::pending), so this branch is the defensive one,
   * and it degrades to exactly the pre-14-14 behaviour rather than to a wrong
   * one.
   */
  const belongsToThisSet = useCallback(
    (mediaRegistryId?: string): boolean =>
      mediaRegistryId !== undefined && tracked.has(mediaRegistryId),
    [tracked],
  );

  // A newly produced candidate arrives as a fresh `initialCandidates` array
  // from the parent's refetch. Merge rather than replace so a tile the user is
  // already looking at keeps its identity, and so a selection in progress is
  // not disturbed by the arrival of a sibling.
  useEffect(() => {
    if (initialCandidates.length === 0) return;
    setCandidates((prev) => {
      const seen = new Set(prev.map((c) => c.media_registry_id));
      const added = initialCandidates.filter(
        (c) => !seen.has(c.media_registry_id),
      );
      return added.length === 0 ? prev : [...prev, ...added];
    });
  }, [initialCandidates]);

  useReverbChannel({
    // SPA-driven: each vision.generation_succeeded frame for this set advances
    // the reveal (RESEARCH Open Question 2). It asks the parent to refetch
    // rather than synthesizing a tile, because the frame has no signed URL in
    // it. Frames for other generations are ignored.
    onVisionSucceeded: (p: VisionSucceededPayload) => {
      if (tracked.size > 0 && !belongsToThisSet(p.mediaRegistryId)) return;
      if (candidates.some((c) => c.media_registry_id === p.mediaRegistryId)) return;
      onCandidateArrived?.();
    },
    // The half that was never designed for. A terminal failure flips the media
    // row to `failed` and broadcasts here; before this, the reveal counted its
    // skeletons purely from `expectedCount - candidates.length` and therefore
    // waited for that candidate forever. Reachable through a provider failure,
    // a content-policy refusal, or a regeneration that exceeds budget, and
    // photorealistic generation makes refusals more likely rather than less.
    onVisionFailed: (p: VisionFailedPayload) => {
      if (!belongsToThisSet(p.mediaRegistryId)) return;
      setFailedIds((prev) => {
        if (prev.has(p.mediaRegistryId as string)) return prev;
        const next = new Set(prev);
        next.add(p.mediaRegistryId as string);
        return next;
      });
    },
  });

  /**
   * The selection survives a reload (operator, 2026-08-16).
   *
   * Deliberately localStorage and NOT the database. Nothing about this choice
   * is committed until she confirms, and confirming is what HARD-DELETES the
   * candidates she did not keep. Writing an in-progress selection server-side
   * would give a half-made decision the same standing as a made one, and the
   * only sensible thing the server could do with it is nothing. A draft belongs
   * to the device the draft is being made on.
   *
   * Keyed by candidate set, so a later set never inherits an earlier set's
   * choice, and every restored id is checked against the candidates actually on
   * screen: a stored id can outlive the row it named.
   */
  useEffect(() => {
    if (restoredRef.current || candidates.length === 0) return;
    restoredRef.current = true;

    type StoredSelection = { primaryId?: unknown; keptIds?: unknown };
    let stored: StoredSelection | null = null;
    try {
      const raw = window.localStorage.getItem(selectionKey(candidateSet));
      stored = raw === null ? null : (JSON.parse(raw) as StoredSelection);
    } catch {
      // Unavailable (Safari private mode) or unparseable. Fall through to the
      // default: a lost draft is a small cost, a thrown reveal is a large one.
      stored = null;
    }
    if (stored === null) return;

    const live = new Set(candidates.map((c) => c.id));
    if (typeof stored.primaryId === 'string' && live.has(stored.primaryId)) {
      setPrimaryId(stored.primaryId);
    }
    if (Array.isArray(stored.keptIds)) {
      setKeptIds(
        new Set(stored.keptIds.filter((id): id is string => typeof id === 'string' && live.has(id))),
      );
    }
  }, [candidates, candidateSet]);

  // Persist on every change. Cheap, and it means the draft survives a crash and
  // a closed tab, not only a deliberate reload.
  useEffect(() => {
    if (!restoredRef.current) return;
    try {
      window.localStorage.setItem(
        selectionKey(candidateSet),
        JSON.stringify({ primaryId, keptIds: [...keptIds] }),
      );
    } catch {
      // Storage full or unavailable. The reveal still works; only the draft is
      // lost, and it is not worth an error surface.
    }
  }, [candidateSet, primaryId, keptIds]);

  // Default the first arrived candidate to primary so confirm is reachable.
  // Runs after the restore effect, which sets `restoredRef` synchronously on
  // its first pass, so a stored choice always wins over this fallback.
  useEffect(() => {
    if (primaryId === null && candidates.length > 0) {
      setPrimaryId(candidates[0].id);
    }
  }, [candidates, primaryId]);

  // ── D-v11-15 funnel (Plan 14-15) ─────────────────────────────────────────
  //
  // The operator kept the trial at three raw generations against a literature
  // estimate, and committed in the same breath to measuring it: pick, abandon,
  // time-to-pick, which ordinal. These three emits are that commitment. They
  // are measurement only and never affect what she sees.
  //
  // Refs rather than state throughout: a re-render must not re-fire an emit,
  // and the cleanup that reports an abandon has to read the values as they were
  // at unmount, not as they were when the effect was declared.
  const shownAtRef = useRef<number | null>(null);
  const pickedRef = useRef(false);
  // Fires once per candidate set, when the reveal first has something to show.
  // Keyed on candidateSet rather than on mount so that abandoning and coming
  // back to a NEW set counts as a second reveal, while a re-render of the same
  // one does not.
  useEffect(() => {
    if (candidates.length === 0) return;
    if (shownAtRef.current !== null) return;

    shownAtRef.current = Date.now();
    void trackEvent({
      event: 'anchor.reveal_shown',
      payload: { candidate_set_id: candidateSet },
    });
  }, [candidates.length, candidateSet]);

  // Abandon is reported from the cleanup, because there is no other moment that
  // means "she left without choosing". It is deliberately NOT fired when she
  // confirmed: handleConfirm sets pickedRef first, and a pick that was also
  // counted as an abandon would make the two rates sum past 100 and quietly
  // ruin the one comparison this instrumentation exists for.
  useEffect(
    () => () => {
      if (shownAtRef.current === null || pickedRef.current) return;

      void trackEvent({
        event: 'anchor.reveal_abandoned',
        payload: {
          candidate_set_id: candidateSet,
          dwell_ms: Date.now() - shownAtRef.current,
        },
      });
    },
    [candidateSet],
  );

  const toggleKeep = useCallback((id: string) => {
    setKeptIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!primaryId) return;
    setSubmitting(true);

    // Recorded before the POST, and the ordering is deliberate. This measures
    // that she CHOSE, which is the funnel outcome D-v11-15 asks about; whether
    // the selection request then succeeded is a different question with its own
    // error path. Recording it after a failed POST would also lose the pick and
    // then count the same visit as an abandon on unmount.
    pickedRef.current = true;

    const chosenIndex = candidates.findIndex((c) => c.id === primaryId);
    void trackEvent({
      event: 'anchor.candidate_picked',
      payload: {
        candidate_set_id: candidateSet,
        // One-based, and among the tiles that ARRIVED rather than among those
        // expected. After Plan 14-14 a partial set is a normal outcome, so
        // arrived_count travels with it: ordinal 2 of 2 is the last one she
        // looked at, ordinal 2 of 3 is the middle, and pooling them would make
        // the ordinal distribution mean nothing.
        ordinal: chosenIndex >= 0 ? chosenIndex + 1 : 1,
        arrived_count: candidates.length,
        time_to_pick_ms:
          shownAtRef.current !== null ? Date.now() - shownAtRef.current : 0,
      },
    });

    try {
      // Backend contract: VisionSelectionRequest validates `primary_vision_id`
      // (required uuid) + `kept_vision_ids` (uuid[]). These MUST match those
      // field names or the select 422s (13.2-06-SUMMARY Issues).
      await api.post(`/api/visions/${candidateSet}/select`, {
        primary_vision_id: primaryId,
        kept_vision_ids: [...keptIds],
      });
      setConfirmOpen(false);
      onConfirmed?.();
    } catch {
      toast.error(t('vision.gallery.loadErrorBody'));
    } finally {
      setSubmitting(false);
    }
  }, [primaryId, candidateSet, candidates, keptIds, onConfirmed, t]);

  // A slot is pending only while it is neither produced nor failed. Failures
  // used to be invisible here, which is precisely how a set with one dead
  // candidate showed a skeleton that never resolved.
  const failedCount = failedIds.size;
  const pendingCount = Math.max(
    0,
    expectedCount - candidates.length - failedCount,
  );
  const somethingFailed = failedCount > 0;
  const everythingFailed = somethingFailed && candidates.length === 0;

  // Nothing arrived and nothing is still coming. Showing the ceremonial frame
  // with an empty rack under it would stage a moment around an absence, so the
  // failure state replaces the ceremony rather than decorating it.
  if (everythingFailed) {
    return (
      <div
        className="flex min-h-screen flex-col items-center justify-center bg-background px-6 py-16 sm:px-12"
        data-testid="reveal-all-failed"
      >
        <h1 className="font-display text-2xl text-foreground">
          {t('vision.reveal.heading')}
        </h1>
        <p className="mt-6 max-w-md text-center font-body text-muted-foreground">
          {t('vision.failure.body')}
        </p>
        <Button
          type="button"
          className="mt-8 min-h-[44px]"
          onClick={() => onCandidateArrived?.()}
        >
          {t('vision.failure.retry')}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-6 py-16 sm:px-12">
      <h1 className="font-display text-2xl text-foreground">
        {t('vision.reveal.heading')}
      </h1>
      <p className="mt-3 max-w-md text-center font-body text-muted-foreground">
        {t('vision.reveal.subline')}
      </p>

      {/*
        The partial set. D-v11-11 at its hardest, and the reason this reuses
        `vision.failure.body` verbatim instead of writing a second sentence for
        the same event: that string already says it was not her fault and that
        it cost her nothing, which is the whole job. A second voice for one
        event is how a product ends up apologising twice.
      */}
      {somethingFailed && (
        <div
          className="mt-6 max-w-md rounded-[0.75rem] border border-border bg-muted/40 p-4 text-center"
          data-testid="reveal-partial-set"
        >
          <p className="font-body text-sm text-muted-foreground">
            {t('vision.failure.body')}
          </p>
        </div>
      )}

      <div className="mt-12 flex flex-wrap justify-center gap-8">
        {candidates.map((candidate) => {
          const isPrimary = candidate.id === primaryId;
          const isKept = keptIds.has(candidate.id);
          return (
            <Card
              key={candidate.id}
              data-testid={`candidate-${candidate.id}`}
              className={
                isPrimary
                  /*
                    The chosen one is BIGGER AND BARE (operator, 2026-08-16).

                    It used to carry a "Hauptbild" pill, a "Als Hauptbild
                    wählen" button in its active state, a ring, and a checked
                    disabled keep box: four ways of saying the same thing to
                    someone who had just said it themselves. All of them are
                    gone. The card is the image, edge to edge, and it is half
                    again the size of the others, so the answer to "which one
                    did I pick" is the first thing the eye lands on rather than
                    a label it has to read.

                    `p-0` and `overflow-hidden` are what let the image reach the
                    card's corners; without the second, the rounded card clips
                    nothing and the image squares them off.
                  */
                  ? 'w-80 overflow-hidden p-0 sm:w-[26rem] lg:w-[32rem]'
                  // Wider than the original w-64. These tiles are where a
                  // person decides whether the face is theirs, and 256px of a
                  // 3:4 portrait leaves the face about 90px tall.
                  : 'flex w-72 flex-col gap-3 p-4 sm:w-80 lg:w-96'
              }
            >
              <ImageZoom
                src={candidate.image_url}
                alt={t('vision.reveal.heading')}
                className={
                  isPrimary
                    ? 'aspect-[3/4] w-full object-cover'
                    : 'aspect-[3/4] w-full rounded-md object-cover'
                }
              />
              {/* No "choose as main image" on the one that already is. The way
                  to change your mind is to press it on a different tile, which
                  is where the question actually is. */}
              {!isPrimary && (
                <Button
                  type="button"
                  variant="outline"
                  className="min-h-[44px]"
                  onClick={() => setPrimaryId(candidate.id)}
                  data-testid={`candidate-pick-${candidate.id}`}
                >
                  {t('vision.reveal.pickPrimary')}
                </Button>
              )}
              {/*
                A CHECKBOX, not a button (operator, 2026-08-16: "the 'Behalten'
                is too subtile"). It was a ghost/secondary toggle whose entire
                on-state signal was a background shade, so the one control that
                decides whether an image survives read as decoration and gave no
                way to tell what it was currently set to.

                Rendered for the OTHER candidates only. The primary carried a
                checked, disabled "Als Hauptbild behalten" box, which said the
                same thing as the ring and the badge already on the tile
                (operator: "actually redundant"). Three ways of saying "this one
                is your main image" is two too many, and a disabled control that
                cannot be acted on is the weakest of the three.
              */}
              {!isPrimary && (
                <label className="flex min-h-[44px] cursor-pointer items-center gap-2 font-body text-sm text-foreground">
                  <Checkbox
                    checked={isKept}
                    onCheckedChange={() => toggleKeep(candidate.id)}
                    data-testid={`candidate-keep-${candidate.id}`}
                  />
                  <span>{t('vision.reveal.keepExtra')}</span>
                </label>
              )}
            </Card>
          );
        })}
        {Array.from({ length: pendingCount }).map((_, i) => (
          <Card key={`pending-${i}`} className="flex w-64 flex-col gap-3 p-6">
            <Skeleton className="aspect-[3/4] w-full rounded-md" />
            <p className="font-body text-sm text-muted-foreground">
              {t('vision.reveal.pending')}
            </p>
          </Card>
        ))}
      </div>

      <Button
        type="button"
        className="mt-12 min-h-[44px]"
        disabled={!primaryId}
        onClick={() => setConfirmOpen(true)}
        data-testid="reveal-confirm"
      >
        {t('vision.reveal.confirm')}
      </Button>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">
              {t('vision.reveal.discardTitle')}
            </DialogTitle>
            <DialogDescription className="font-body">
              {t('vision.reveal.discardBody')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmOpen(false)}
            >
              {t('vision.reveal.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={submitting}
              onClick={() => void handleConfirm()}
              data-testid="reveal-discard-confirm"
            >
              {t('vision.reveal.discardConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
